import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import type { OrganizationSubscriptionInstanceDto } from '@/types/dtos/organizations/organization-subscription-instance.dto';
import type {
  SubscriptionBillingPeriod,
  SubscriptionCategory,
  SubscriptionPlan,
  SubscriptionTier,
} from '@/types/dtos/transactions/subscription-plan.dto';
import { SUBSCRIPTION_CATEGORY } from '@/types/dtos/transactions/subscription-plan.dto';

export const TIER_ORDER: SubscriptionTier[] = ['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM'];

export const SUBSCRIPTION_CATALOG_CATEGORY_ORDER: SubscriptionCategory[] = [...SUBSCRIPTION_CATEGORY];

export function subscriptionTierRank(t: string | undefined): number {
  if (!t) {
    return -1;
  }
  const i = TIER_ORDER.indexOf(t as SubscriptionTier);
  return i;
}

/** Canonical `*_default` product key for the main line in a category (e.g. HOST → `host_default`). */
export function defaultMainSubscriptionProductKeyForCategory(category: SubscriptionCategory): string {
  return `${category.toLowerCase()}_default`;
}

/** Host default product key derived from category — used as a normalisation fallback. */
const HOST_DEFAULT_PRODUCT_KEY = defaultMainSubscriptionProductKeyForCategory('HOST');

export function normalizeSubscriptionProductKey(plan: SubscriptionPlan): string {
  return (plan.subscriptionProductKey?.trim() || defaultMainSubscriptionProductKeyForCategory('HOST')) as string;
}

function inferCategoryFromMainProductKey(productKey: string | undefined): SubscriptionCategory | undefined {
  const pk = (productKey ?? '').trim();
  const m = /^([a-z]+)_default$/i.exec(pk);
  if (!m) {
    return undefined;
  }
  const upper = m[1].toUpperCase();
  return (SUBSCRIPTION_CATEGORY as readonly string[]).includes(upper) ? (upper as SubscriptionCategory) : undefined;
}

export function subscriptionCategoryKey(plan: SubscriptionPlan): SubscriptionCategory {
  const raw = plan.subscriptionCategory;
  if (String(raw) === 'SERVICE_PROVIDER') {
    return 'OTHER';
  }
  if (raw && (SUBSCRIPTION_CATEGORY as readonly string[]).includes(raw)) {
    return raw;
  }
  const fromKey = inferCategoryFromMainProductKey(normalizeSubscriptionProductKey(plan));
  if (fromKey) {
    return fromKey;
  }
  return 'OTHER';
}

/** One booked main subscription per category (no parent), ordered for nav / summaries. */
export type BookedMainPlanSummaryEntry = {
  subscriptionProductKey: string;
  category: SubscriptionCategory;
  tier: string;
};

export function bookedMainSubscriptionSummaryEntries(org: OrganizationDto | undefined): BookedMainPlanSummaryEntry[] {
  const subs = org?.subscriptions ?? [];
  const mains = subs.filter((s) => !s.parentSubscriptionInstanceId && s.subscriptionPlan?.requiresParent !== true);
  const byCat = new Map<SubscriptionCategory, (typeof subs)[0]>();
  for (const s of mains) {
    const plan = s.subscriptionPlan;
    if (!plan) {
      continue;
    }
    const cat = subscriptionCategoryKey(plan);
    if (!byCat.has(cat)) {
      byCat.set(cat, s);
    }
  }
  const entries: BookedMainPlanSummaryEntry[] = [];
  for (const cat of SUBSCRIPTION_CATALOG_CATEGORY_ORDER) {
    if (!byCat.has(cat)) {
      continue;
    }
    const s = byCat.get(cat)!;
    const plan = s.subscriptionPlan!;
    const tier = (plan.tier ?? s.tier ?? 'FREE') as string;
    const subscriptionProductKey = (s.subscriptionProductKey?.trim() ||
      normalizeSubscriptionProductKey(plan)) as string;
    entries.push({ subscriptionProductKey, category: cat, tier });
  }
  return entries;
}

/** One line per category for booked main subscriptions (no parent), ordered for nav / summaries. */
export function bookedMainSubscriptionSummaryLines(
  org: OrganizationDto | undefined,
  categoryLabel: (category: SubscriptionCategory) => string,
  tierLabel: (tier: string) => string,
): string[] {
  return bookedMainSubscriptionSummaryEntries(org).map((e) => `${categoryLabel(e.category)} - ${tierLabel(e.tier)}`);
}

export type CatalogProductLine = {
  subscriptionProductKey: string;
  isAddon: boolean;
  plans: SubscriptionPlan[];
};

/**
 * Group Organization catalog plans by `subscriptionCategory`, then by main line vs add-on
 * (`requiresParent`) and stable `subscriptionProductKey`. Categories with no plans are omitted.
 */
export function groupOrganizationProductLines(
  plans: SubscriptionPlan[] | undefined,
): Map<SubscriptionCategory, { main: CatalogProductLine[]; addOn: CatalogProductLine[] }> {
  const byCat = new Map<
    SubscriptionCategory,
    { main: Map<string, CatalogProductLine>; addOn: Map<string, CatalogProductLine> }
  >();
  for (const c of SUBSCRIPTION_CATEGORY) {
    byCat.set(c, { main: new Map(), addOn: new Map() });
  }

  for (const plan of plans ?? []) {
    if (plan.entityType !== 'Organization') {
      continue;
    }
    const category = subscriptionCategoryKey(plan);
    const pk = normalizeSubscriptionProductKey(plan);
    const isAddon = plan.requiresParent === true;
    const entry = byCat.get(category) ?? byCat.get('OTHER')!;
    const map = isAddon ? entry.addOn : entry.main;
    const existing = map.get(pk);
    if (existing) {
      existing.plans.push(plan);
    } else {
      map.set(pk, { subscriptionProductKey: pk, isAddon, plans: [plan] });
    }
  }

  const out = new Map<SubscriptionCategory, { main: CatalogProductLine[]; addOn: CatalogProductLine[] }>();
  for (const [cat, { main, addOn }] of byCat) {
    const m = [...main.values()].map(mergeAndSort);
    const a = [...addOn.values()].map(mergeAndSort);
    if (m.length || a.length) {
      out.set(cat, { main: m, addOn: a });
    }
  }
  return out;
}

function mergeAndSort(line: CatalogProductLine): CatalogProductLine {
  return { ...line, plans: sortPlanRows(line.plans) };
}

function sortPlanRows(plans: SubscriptionPlan[]): SubscriptionPlan[] {
  return [...plans].sort((a, b) => {
    const tr = subscriptionTierRank(a.tier) - subscriptionTierRank(b.tier);
    if (tr !== 0) {
      return tr;
    }
    const ab = a.billingPeriod === 'YEAR' ? 1 : 0;
    const bb = b.billingPeriod === 'YEAR' ? 1 : 0;
    return ab - bb;
  });
}

export function pickDefaultBillingForProductLine(line: CatalogProductLine): 'MONTH' | 'YEAR' {
  const hasMonth = line.plans.some((p) => (p.billingPeriod ?? 'MONTH') === 'MONTH');
  return hasMonth ? 'MONTH' : 'YEAR';
}

/**
 * Default billing when adding or previewing an add-on: if the **parent** main line is yearly (booked org
 * subscription or draft checkout line), prefer yearly for the add-on when the catalog exposes a YEAR row.
 */
export function pickDefaultBillingForAddonProductLine(
  line: CatalogProductLine,
  org: OrganizationDto | undefined,
  cartLines: { subscriptionProductKey?: string; billingPeriod?: string }[] | undefined,
): 'MONTH' | 'YEAR' {
  if (!line.isAddon) {
    return pickDefaultBillingForProductLine(line);
  }
  const marker = line.plans.find((p) => p.requiresParent === true);
  const parentPk = (marker?.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) as string;
  const parentBp = resolveParentBillingPeriodForAddonFromOrgAndCart(org, cartLines, parentPk);
  if (parentBp === 'YEAR') {
    const hasYear = line.plans.some((p) => (p.billingPeriod ?? 'MONTH') === 'YEAR');
    if (hasYear) {
      return 'YEAR';
    }
  }
  return pickDefaultBillingForProductLine(line);
}

export function plansForBillingWindow(line: CatalogProductLine, billing: 'MONTH' | 'YEAR'): SubscriptionPlan[] {
  return line.plans.filter((p) => (p.billingPeriod ?? 'MONTH') === billing);
}

export function countUniqueTiersInBillingWindow(line: CatalogProductLine, billing: 'MONTH' | 'YEAR'): number {
  return new Set(plansForBillingWindow(line, billing).map((p) => p.tier)).size;
}

/** Distinct tiers for this product across all billing rows (used when tier choice is meaningless). */
export function uniqueTierCountForProductLine(line: CatalogProductLine | undefined): number {
  if (!line?.plans?.length) {
    return 0;
  }
  return new Set(line.plans.map((p) => p.tier)).size;
}

/** When the catalog exposes only one tier for this product, callers may omit `tier` on checkout cart PATCH. */
export function soleTierForProductLine(line: CatalogProductLine | undefined): SubscriptionTier | undefined {
  if (!line?.plans?.length) {
    return undefined;
  }
  const tiers = [...new Set(line.plans.map((p) => p.tier))] as SubscriptionTier[];
  return tiers.length === 1 ? tiers[0] : undefined;
}

export function findOrganizationInstanceForProduct(
  org: OrganizationDto | undefined,
  productKey: string,
): OrganizationSubscriptionInstanceDto | undefined {
  const want = productKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  return org?.subscriptions?.find((s) => {
    if (s.entityType !== 'Organization') {
      return false;
    }
    const sk = (s.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) as string;
    return sk === want;
  });
}

/**
 * Effective parent billing cadence for add-on rules: **draft checkout line for the parent wins** when that
 * product is in the cart (selected cadence), otherwise the **booked** org subscription row. Defaults to
 * monthly when the parent is not in the cart and not on the org.
 */
export function resolveParentBillingPeriodForAddonFromOrgAndCart(
  org: OrganizationDto | undefined,
  cartLines: { subscriptionProductKey?: string; billingPeriod?: string }[] | undefined,
  parentProductKey: string,
): SubscriptionBillingPeriod {
  const pk = parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const cartParent = (cartLines ?? []).find(
    (l) => (l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === pk,
  );
  if (cartParent) {
    const raw = (cartParent.billingPeriod ?? 'MONTH') as string;
    return raw === 'YEAR' ? 'YEAR' : 'MONTH';
  }
  const inst = findOrganizationInstanceForProduct(org, pk);
  if (inst) {
    const plan = inst.subscriptionPlan as SubscriptionPlan | undefined;
    const raw = (inst.billingPeriod ?? plan?.billingPeriod ?? 'MONTH') as string;
    return raw === 'YEAR' ? 'YEAR' : 'MONTH';
  }
  return 'MONTH';
}

function parentMeetsMinTier(instanceTier: string | undefined, minParentTier: SubscriptionTier | undefined): boolean {
  if (!minParentTier) {
    return true;
  }
  return subscriptionTierRank(instanceTier) >= subscriptionTierRank(minParentTier);
}

/**
 * For add-on catalog lines: resolve a parent `Organization` subscription line id when `requiresParent` is set,
 * or accept a qualifying **draft** parent line in `subscriptionCheckoutCart` (same product key) before
 * the org is updated. For main product lines, returns an empty object (no parent on the cart line).
 */
export function resolveAddOnParentInstanceId(
  org: OrganizationDto | undefined,
  catalogLine: Pick<SubscriptionPlan, 'requiresParent' | 'parentProductKey' | 'minParentTier'>,
  /** Same checkout as on the plan page or in the checkout modal (optional product lines). */
  cartLines?: { subscriptionProductKey?: string; tier?: string }[] | undefined,
):
  | Record<string, never>
  | { parentSubscriptionInstanceId: string }
  | { parentSatisfiedInCart: true }
  | { error: 'missing_parent' | 'parent_tier' } {
  if (!catalogLine.requiresParent) {
    return {};
  }
  const min = catalogLine.minParentTier;
  const parentPk = catalogLine.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const orgParent = findOrganizationInstanceForProduct(org, parentPk);
  const orgTier = orgParent?.tier ?? (orgParent?.subscriptionPlan as SubscriptionPlan | undefined)?.tier;
  const cartParent = (cartLines ?? []).find(
    (l) => (l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === parentPk,
  );
  const cartTier = cartParent?.tier;

  if (orgParent?.id && parentMeetsMinTier(orgTier, min)) {
    return { parentSubscriptionInstanceId: orgParent.id };
  }
  if (cartTier && parentMeetsMinTier(cartTier, min)) {
    return { parentSatisfiedInCart: true };
  }
  if (orgParent?.id) {
    return { error: 'parent_tier' };
  }
  if (cartTier) {
    return { error: 'parent_tier' };
  }
  return { error: 'missing_parent' };
}

/** Strongest `minParentTier` among catalog rows for this add-on (billing/tier variants may omit it on some rows). */
function maxMinParentTierAcrossAddonLine(line: CatalogProductLine): SubscriptionTier | undefined {
  let best: SubscriptionTier | undefined;
  let bestRank = -1;
  for (const p of line.plans) {
    if (!p.requiresParent || !p.minParentTier) {
      continue;
    }
    const r = subscriptionTierRank(p.minParentTier);
    if (r > bestRank) {
      bestRank = r;
      best = p.minParentTier;
    }
  }
  return best;
}

/**
 * When the add-on cannot be satisfied yet (parent missing, or parent/cart tier below `minParentTier`),
 * returns that catalog `minParentTier` for copy such as “Requires Premium plan.”
 */
export function minParentTierWhenAddonParentInsufficient(
  org: OrganizationDto | undefined,
  line: CatalogProductLine,
  cartLines?: { subscriptionProductKey?: string; tier?: string }[] | undefined,
): SubscriptionTier | undefined {
  if (!line.isAddon) {
    return undefined;
  }
  const marker = line.plans.find((p) => p.requiresParent === true);
  const minTier = maxMinParentTierAcrossAddonLine(line);
  if (!marker?.requiresParent || !minTier) {
    return undefined;
  }
  const catalogLineForResolve: Pick<SubscriptionPlan, 'requiresParent' | 'parentProductKey' | 'minParentTier'> = {
    requiresParent: true,
    parentProductKey: marker.parentProductKey,
    minParentTier: minTier,
  };
  const outcome = resolveAddOnParentInstanceId(org, catalogLineForResolve, cartLines);
  if (!('error' in outcome)) {
    return undefined;
  }
  return minTier;
}
